import { useMemo } from 'react';
import { Combobox, type ComboboxOption } from '@/components/combobox';
import { useMetalTradeCandidates, type MetalTradeCandidate } from '../api/metal-trade-candidates';

export interface MetalRefPickerProps {
  value: string | null;
  onChange: (tradeId: string | null) => void;
  // Narrows candidates to the metal being allocated; omit to list all metals.
  metal?: string | undefined;
  disabled?: boolean | undefined;
  // Defer the network fetch until the picker is actually shown.
  enabled?: boolean | undefined;
  // Accept a hand-typed reference alongside the list. The feed only refreshes
  // on a sync, so a fill executed minutes ago may not be listed yet — without
  // this the desk would be blocked until the next sync.
  allowFreeText?: boolean | undefined;
  className?: string | undefined;
  'aria-label'?: string | undefined;
  id?: string | undefined;
}

const QUANTITY_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const PRICE_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

// One scannable line so an operator can match a trade by metal, size, price and
// date without leaving the picker: "2026081800000LPT · Copper Buy · 150 MT @ 14,850 · 2026-08-18".
function candidateLabel(trade: MetalTradeCandidate): string {
  const parts = [trade.tradeId];
  const metalSide = [trade.underlyer, trade.side].filter(Boolean).join(' ');
  if (metalSide) parts.push(metalSide);
  if (trade.quantityMt !== null) {
    const quantity = `${QUANTITY_FORMAT.format(Number(trade.quantityMt))} MT`;
    parts.push(
      trade.price !== null ? `${quantity} @ ${PRICE_FORMAT.format(Number(trade.price))}` : quantity,
    );
  }
  if (trade.tradeDate !== null) parts.push(trade.tradeDate);
  return parts.join(' · ');
}

// A creatable picker suppresses the shared empty message and only shows the
// add row once something is typed, so zero candidates renders an empty popover
// that reads as broken. This non-selectable row says what to do instead — a
// fill booked since the last broker sync isn't listed yet.
const TYPE_TO_ENTER_HINT: ComboboxOption = {
  value: '__metal_ref_hint__',
  label: 'No broker trades to choose from — type the reference.',
  disabled: true,
};

function buildOptions(
  candidates: MetalTradeCandidate[] | undefined,
  value: string | null,
  allowFreeText: boolean,
): ComboboxOption[] {
  const list = Array.isArray(candidates) ? candidates : [];
  const options = list.map((trade) => ({
    value: trade.tradeId,
    label: candidateLabel(trade),
  }));
  // Keep a currently-set ref visible even if it's outside the candidate window.
  if (value !== null && !options.some((option) => option.value === value)) {
    return [{ value, label: value }, ...options];
  }
  if (options.length === 0 && allowFreeText) {
    return [TYPE_TO_ENTER_HINT];
  }
  return options;
}

// Replaces the free-text contract-reference input on metal fills: the operator
// picks a real StoneX broker trade, so the reference is always a valid id (the
// wrong-typed-reference bug can't recur) — the metals mirror of FxRefPicker.
export function MetalRefPicker({
  value,
  onChange,
  metal,
  disabled,
  enabled = true,
  allowFreeText = false,
  className,
  id,
  'aria-label': ariaLabel = 'StoneX broker trade',
}: Readonly<MetalRefPickerProps>) {
  const { data, isLoading } = useMetalTradeCandidates(metal, enabled);
  const options = useMemo(
    () => buildOptions(data, value, allowFreeText),
    [data, value, allowFreeText],
  );
  const onCreate = allowFreeText
    ? (typed: string) => {
        const trimmed = typed.trim();
        onChange(trimmed === '' ? null : trimmed);
      }
    : undefined;

  return (
    <Combobox
      options={options}
      value={value ?? undefined}
      onChange={(next) => {
        onChange(next ?? null);
      }}
      {...(onCreate ? { onCreate } : {})}
      disabled={disabled}
      loading={isLoading}
      loadingMessage="Loading broker trades…"
      emptyMessage="No matching broker trades."
      placeholder="Select broker trade…"
      searchPlaceholder="Search by ref, size, date…"
      className={className}
      id={id}
      aria-label={ariaLabel}
    />
  );
}
